/**
 * build-detail.js — TFS Wheels Customer Build Detail Page
 *
 * Reads ?id= from window.location.search, fetches
 * GET {api_base}/api/gallery/builds/<id>, and renders:
 *   - Heading: year make model [submodel]
 *   - Image gallery: primary image + thumbnail strip, click to swap
 *   - Vehicle/fitment summary: fitment_type + fitment_detail
 *   - Wheels & Tires: build.products[] (resolved products link to store)
 *   - Reviews: rating stars + title + body + reviewer_name + date
 *
 * Security: all API-sourced strings are set via textContent or validated
 * before use as attribute values. No innerHTML with user/API-controlled
 * content. DOM is built via createElement (same pattern as gallery-index.js
 * and gallery-submit.js).
 *
 * Config: data-api-base on the #tfs-build-detail container.
 * Default API base: https://tfs-manager-server-production.up.railway.app
 */

(function () {
  'use strict';

  var DEFAULT_API_BASE = 'https://tfs-manager-server-production.up.railway.app';
  var GALLERY_PAGE     = '/pages/vehicle-gallery';

  /** Safe image origins accepted for display. */
  var SAFE_IMG_ORIGINS = ['cdn.shopify.com', 'cdn.shopifycdn.com'];

  /** Safe product URL origins (relative paths on the store are also accepted). */
  var SAFE_PRODUCT_ORIGINS = ['tfswheels.com', 'www.tfswheels.com'];

  // ── DOM refs ──────────────────────────────────────────────────────────────────
  var container = document.getElementById('tfs-build-detail');
  if (!container) return;

  var API_BASE = (container.getAttribute('data-api-base') || DEFAULT_API_BASE).replace(/\/$/, '');

  // ── URL helpers ───────────────────────────────────────────────────────────────

  /**
   * Validate a URL string as a safe https image from a known CDN.
   * Returns the URL string if safe, null otherwise.
   */
  function safeImgUrl(raw) {
    if (!raw || typeof raw !== 'string') return null;
    try {
      var u = new URL(raw);
      if (u.protocol !== 'https:') return null;
      var host = u.hostname.toLowerCase();
      for (var i = 0; i < SAFE_IMG_ORIGINS.length; i++) {
        if (host === SAFE_IMG_ORIGINS[i] || host.endsWith('.' + SAFE_IMG_ORIGINS[i])) return raw;
      }
      return null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Validate a product URL string as a safe https link to the store or a
   * relative /products/ path. Returns the URL string if safe, null otherwise.
   */
  function safeProductUrl(raw) {
    if (!raw || typeof raw !== 'string') return null;
    // Relative /products/... paths are always safe.
    if (/^\/products\/[a-zA-Z0-9_-]/.test(raw)) return raw;
    try {
      var u = new URL(raw);
      if (u.protocol !== 'https:') return null;
      var host = u.hostname.toLowerCase();
      for (var i = 0; i < SAFE_PRODUCT_ORIGINS.length; i++) {
        if (host === SAFE_PRODUCT_ORIGINS[i]) {
          // Only allow /products/ paths to prevent open-redirect.
          if (/^\/products\//.test(u.pathname)) return raw;
        }
      }
      return null;
    } catch (_) {
      return null;
    }
  }

  // ── State ─────────────────────────────────────────────────────────────────────
  var _images    = [];  // ordered image URLs (primary first)
  var _activeIdx = 0;

  // ── DOM builders ─────────────────────────────────────────────────────────────

  /** Replace all children of a node. */
  function setChildren(parent) {
    var nodes = Array.prototype.slice.call(arguments, 1);
    while (parent.firstChild) parent.removeChild(parent.firstChild);
    nodes.forEach(function (n) { if (n) parent.appendChild(n); });
  }

  /** Create element with optional class and textContent. */
  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls)  e.className   = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  /** Render a friendly "not found / error" message with a back link. */
  function renderError(message) {
    var wrap = el('div', 'bd-error');
    var p    = el('p', null, message + ' ');
    var a    = el('a', null, 'Back to Gallery');
    a.href   = GALLERY_PAGE;
    p.appendChild(a);
    wrap.appendChild(p);
    setChildren(container, wrap);
  }

  /** Render a spinner/loading placeholder. */
  function renderLoading() {
    var div = el('div', 'bd-loading', 'Loading build…');
    setChildren(container, div);
  }

  // ── Image gallery ─────────────────────────────────────────────────────────────

  var _mainImgEl   = null;
  var _thumbsEl    = null;

  function buildImageGallery(images) {
    var safe = images
      .map(function (img) { return safeImgUrl(img.shopify_file_url || img.url || img); })
      .filter(Boolean);
    _images = safe;

    if (safe.length === 0) return null;

    var section = el('div', 'bd-gallery');

    // Main image
    var mainWrap = el('div', 'bd-gallery-main');
    var mainImg  = document.createElement('img');
    mainImg.className = 'bd-gallery-main-img';
    mainImg.alt       = 'Build photo';
    mainImg.src       = safe[0] + (safe[0].includes('?') ? '&' : '?') + 'width=900';
    mainImg.width     = 900;
    mainWrap.appendChild(mainImg);
    _mainImgEl = mainImg;

    section.appendChild(mainWrap);

    // Thumbnails (only if more than one image)
    if (safe.length > 1) {
      var thumbsRow = el('div', 'bd-gallery-thumbs');
      safe.forEach(function (url, idx) {
        var thumbBtn = document.createElement('button');
        thumbBtn.type      = 'button';
        thumbBtn.className = 'bd-thumb-btn' + (idx === 0 ? ' bd-thumb-btn--active' : '');
        thumbBtn.setAttribute('aria-label', 'View photo ' + (idx + 1));
        thumbBtn.setAttribute('data-idx', String(idx));

        var thumbImg = document.createElement('img');
        thumbImg.className = 'bd-thumb-img';
        thumbImg.alt       = 'Thumbnail ' + (idx + 1);
        thumbImg.src       = url + (url.includes('?') ? '&' : '?') + 'width=150';
        thumbImg.width     = 150;
        thumbImg.loading   = 'lazy';

        thumbBtn.appendChild(thumbImg);
        thumbBtn.addEventListener('click', function () { activateThumb(idx); });
        thumbsRow.appendChild(thumbBtn);
      });
      _thumbsEl = thumbsRow;
      section.appendChild(thumbsRow);
    }

    return section;
  }

  function activateThumb(idx) {
    if (!_images[idx] || !_mainImgEl) return;
    _activeIdx = idx;
    var url = _images[idx];
    _mainImgEl.src = url + (url.includes('?') ? '&' : '?') + 'width=900';

    if (_thumbsEl) {
      var btns = _thumbsEl.querySelectorAll('.bd-thumb-btn');
      btns.forEach(function (btn, i) {
        btn.className = 'bd-thumb-btn' + (i === idx ? ' bd-thumb-btn--active' : '');
      });
    }
  }

  // ── Vehicle / fitment summary ──────────────────────────────────────────────────

  function buildVehicleSection(build) {
    // Heading: year make model [submodel] — endpoint returns FLAT vehicle_* fields
    var parts = [build.vehicle_year, build.vehicle_make, build.vehicle_model, build.vehicle_submodel].filter(Boolean);
    var titleText = parts.join(' ') || 'Customer Build';

    var heading = el('h1', 'bd-heading', titleText);

    // Display name / submitter
    var meta = el('div', 'bd-meta');
    if (build.display_name) {
      var submitter = el('span', 'bd-meta-submitter', 'Submitted by ' + build.display_name);
      meta.appendChild(submitter);
    }

    // Fitment badge
    if (build.fitment_type) {
      var fitLabel = {
        stock:     'Stock',
        lift:      'Lifted',
        leveling:  'Leveling',
        springs:   'Spring Upgrade',
        coilovers: 'Coilovers',
        lowered:   'Lowered',
        other:     'Other',
      }[build.fitment_type] || build.fitment_type;

      var badge = el('span', 'bd-fitment-badge', fitLabel);
      meta.appendChild(badge);
    }

    var section = el('div', 'bd-vehicle-section');
    section.appendChild(heading);
    section.appendChild(meta);

    // Fitment detail
    if (build.fitment_detail) {
      var fitDetail = el('p', 'bd-fitment-detail', build.fitment_detail);
      section.appendChild(fitDetail);
    }

    return section;
  }

  // ── Products (Wheels & Tires) ─────────────────────────────────────────────────

  function buildProductsSection(products) {
    if (!products || products.length === 0) return null;

    var section = el('div', 'bd-products-section');
    var heading  = el('h2', 'bd-section-heading', 'Wheels & Tires');
    section.appendChild(heading);

    var list = el('ul', 'bd-products-list');

    products.forEach(function (product) {
      var item   = el('li', 'bd-product-item');
      var kind   = (product.kind || 'wheel').toLowerCase();
      var icon   = el('span', 'bd-product-kind bd-product-kind--' + kind, kind === 'tire' ? 'Tire' : 'Wheel');

      // Build label: brand + model + size
      var labelParts = [];
      if (product.brand || product.vendor)  labelParts.push(product.brand || product.vendor);
      if (product.wheel_model)              labelParts.push(product.wheel_model);
      var sizeParts = [];
      if (product.diameter) sizeParts.push(product.diameter + '"');
      if (product.width)    sizeParts.push('x' + product.width);
      if (sizeParts.length) labelParts.push(sizeParts.join(''));
      var labelText = labelParts.join(' · ') || 'Unknown product';

      // Thumbnail
      var thumbSrc = safeImgUrl(product.resolved_image_url);
      if (thumbSrc) {
        var thumb = document.createElement('img');
        thumb.className = 'bd-product-thumb';
        thumb.src       = thumbSrc + (thumbSrc.includes('?') ? '&' : '?') + 'width=80';
        thumb.alt       = labelText;
        thumb.width     = 80;
        thumb.loading   = 'lazy';
        item.appendChild(thumb);
      }

      item.appendChild(icon);

      // If the product is resolved and has a handle, link to the PDP
      var productUrl = safeProductUrl(product.product_url || product.resolved_handle && '/products/' + product.resolved_handle);
      if ((product.is_resolved || product.resolved_handle) && productUrl) {
        var link = el('a', 'bd-product-name', labelText);
        link.href   = productUrl;
        link.target = '_blank';
        link.rel    = 'noopener noreferrer';
        item.appendChild(link);
      } else {
        item.appendChild(el('span', 'bd-product-name', labelText));
      }

      list.appendChild(item);
    });

    section.appendChild(list);
    return section;
  }

  // ── Reviews ───────────────────────────────────────────────────────────────────

  function buildStars(rating) {
    var wrap = el('span', 'bd-stars');
    var r    = Math.round(parseFloat(rating) || 0);
    for (var i = 1; i <= 5; i++) {
      var star = el('span', i <= r ? 'bd-star bd-star--filled' : 'bd-star', i <= r ? '★' : '☆');
      wrap.appendChild(star);
    }
    return wrap;
  }

  function buildReviewsSection(reviews) {
    if (!reviews || reviews.length === 0) return null;

    var section = el('div', 'bd-reviews-section');
    var heading  = el('h2', 'bd-section-heading', 'Customer Review' + (reviews.length !== 1 ? 's' : ''));
    section.appendChild(heading);

    var list = el('div', 'bd-reviews-list');

    reviews.forEach(function (review) {
      var card = el('div', 'bd-review-card');

      // Stars + rating
      var header = el('div', 'bd-review-header');
      if (review.rating != null) {
        header.appendChild(buildStars(review.rating));
      }

      // Reviewer name + date
      var byline = el('div', 'bd-review-byline');
      if (review.reviewer_name) {
        byline.appendChild(el('span', 'bd-reviewer-name', review.reviewer_name));
      }
      if (review.created_at || review.submitted_at) {
        var raw  = review.created_at || review.submitted_at;
        var date = '';
        try {
          date = new Date(raw).toLocaleDateString('en-US', {
            timeZone: 'America/New_York',
            year:     'numeric',
            month:    'long',
            day:      'numeric',
          });
        } catch (_) {
          date = raw;
        }
        byline.appendChild(el('span', 'bd-review-date', date));
      }

      header.appendChild(byline);
      card.appendChild(header);

      // Title
      if (review.title) {
        card.appendChild(el('p', 'bd-review-title', review.title));
      }

      // Body
      if (review.body || review.review_text) {
        card.appendChild(el('p', 'bd-review-body', review.body || review.review_text));
      }

      list.appendChild(card);
    });

    section.appendChild(list);
    return section;
  }

  // ── Main render ───────────────────────────────────────────────────────────────

  function renderBuild(build) {
    // Collect images: prefer build.images[], fall back to build.primary_image_url
    var rawImages = [];
    if (Array.isArray(build.images) && build.images.length > 0) {
      // Sort: primary first, then the rest
      var sorted = build.images.slice().sort(function (a, b) {
        return (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0);
      });
      rawImages = sorted;
    } else if (build.primary_image_url) {
      rawImages = [{ shopify_file_url: build.primary_image_url }];
    }

    var gallery  = buildImageGallery(rawImages);
    var vehicle  = buildVehicleSection(build);
    var products = buildProductsSection(build.products || build.wheels || []);
    var reviews  = buildReviewsSection(build.reviews || []);

    // Back link
    var backRow = el('div', 'bd-back-row');
    var backA   = el('a', 'bd-back-link', '← All Builds');
    backA.href  = GALLERY_PAGE;
    backRow.appendChild(backA);

    var inner = el('div', 'bd-inner');

    inner.appendChild(backRow);
    inner.appendChild(vehicle);

    if (gallery) inner.appendChild(gallery);
    if (products) inner.appendChild(products);
    if (reviews)  inner.appendChild(reviews);

    setChildren(container, inner);
  }

  // ── Boot: read ?id=, fetch build ─────────────────────────────────────────────

  var params = new URLSearchParams(window.location.search);
  var publicId = params.get('id');

  if (!publicId || !/^[0-9a-f-]{36}$/.test(publicId)) {
    // Missing or malformed id — /pages/build is only meaningful with a build ?id=.
    // Send stray visits to the gallery instead of showing an error page.
    window.location.replace(GALLERY_PAGE);
  } else {
    renderLoading();

    var url = API_BASE + '/api/gallery/builds/' + encodeURIComponent(publicId);

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (r.status === 404) throw new Error('not_found');
        if (!r.ok)            throw new Error('http_' + r.status);
        return r.json();
      })
      .then(function (data) {
        var build = data.data || data.build || data;
        if (!build || data.success === false) throw new Error('not_found');
        renderBuild(build);
      })
      .catch(function (err) {
        if (err && err.message === 'not_found') {
          renderError('Build not found.');
        } else {
          renderError('Could not load this build. Please try again later.');
        }
      });
  }

})();
